import React, { useCallback, useContext, useMemo } from 'react';
import { gql } from '@urql/core';

import { Layout } from 'components/common/layout/Layout';
import {
    CollegeCardFragment,
    Colleges_IndexDocument,
    Colleges_IndexQuery,
    Espin_Address_Set_Input,
    Espin_College_Bool_Exp,
    Espin_Role_Enum,
    Espin_Subscription_Status_Enum,
    SearchCollegesDocument,
    SearchCollegesQuery,
    SearchCollegesQueryVariables,
} from 'generated/spin-graphql';
import { mixpanelRecordEvent } from 'components/analytics/analyticsWrapper';
import { UrqlSSGForPage } from 'server/utils/serverUtils/ssg.utils';
import { PageHeader } from '../../components/sections/PageHeader';
import { PageListHeader } from '../../components/sections/PageListHeader';

import { useClient, useQuery } from 'urql';
import { createSearchBarProvider, SearchActionType, SearchBarContext, SearchBarContextType } from '../../client/hooks';
import { WritableDraft } from 'immer/dist/internal';
import { CustomSEO } from '../../components/common/layout/HeadMeta';

import {
    CollegeSearchFilterComponent,
    CollegeSearchFilterState,
} from '../../components/colleges/search/CollegeSearchFilterComponent';
import { useImmer } from 'use-immer';
import { useAppState } from '../../client/providers/AppProvider';
import { toast } from 'react-toastify';
import { useCollegeSearchHistoryProvider } from '../../client/providers/search/CollegeSearchHistoryProvider';
import { COLLEGES_PER_PAGE, CONVERT_TO_MILES } from '../../shared/shared.constants';
import { useEffectOnLoad } from '../../client/hooks/use-effect-once';
import { CollegeSearchResultsDisplay } from '../../components/colleges/search/CollegeSearchResultsDisplay';
import { MixpanelEvents } from '../../shared/shared.types';

gql`
    query Colleges_Index {
        ...CollegeSearchFilterComponent
        subbedCount: espin_college_aggregate(
            where: {
                _and: [
                    { verified: { _eq: true } }
                    { slug: { _is_null: false } }
                    { _or: [{ subscribed: { _eq: true } }, { subscription: { status: { _eq: active } } }] }
                ]
            }
            order_by: { name: asc }
        ) {
            aggregate {
                count
            }
        }
    }

    query SearchColleges(
        $subbedSearch: espin_college_bool_exp!
        $unsubbedSearch: espin_college_bool_exp!
        $subbedOffset: Int! = 0
        $subbedPageSize: Int! = 50
        $unsubbedOffset: Int! = 0
        $unsubbedPageSize: Int! = 50
    ) {
        unsubbedColleges: espin_college(
            where: $unsubbedSearch
            order_by: { name: asc }
            limit: $unsubbedPageSize
            offset: $unsubbedOffset
        ) {
            ...CollegeCard
            id
            name
            program_name
            subscribed
            address {
                city
                state
                country
            }
        }
        subbedColleges: espin_college(
            where: $subbedSearch
            order_by: { name: asc }
            limit: $subbedPageSize
            offset: $subbedOffset
        ) {
            ...CollegeCard
            id
            name
            program_name
            subscribed
            address {
                city
                state
                country
            }
        }
        subbedCount: espin_college_aggregate(
            where: {
                _and: [
                    { verified: { _eq: true } }
                    { slug: { _is_null: false } }
                    { _or: [{ subscribed: { _eq: true } }, { subscription: { status: { _eq: active } } }] }
                ]
            }
            order_by: { name: asc }
        ) {
            aggregate {
                count
            }
        }
        allCount: espin_college_aggregate(where: { _or: [$subbedSearch, $unsubbedSearch] }, order_by: { name: asc }) {
            aggregate {
                count
            }
        }
    }
`;

export default function Colleges_Index() {
    const { user } = useAppState();
    const userAddress: Espin_Address_Set_Input | null = useMemo(() => {
        if (user) {
            if (user.role === Espin_Role_Enum.EspinCollegeUser) {
                return user.college_profile?.college.address;
            } else if (user.role === Espin_Role_Enum.EspinPlayerUser) {
                return user.player_profile?.address;
            } else if (user.role === Espin_Role_Enum.EspinParentUser) {
                return user.parent_profile?.address;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }, [user]);

    const gqlClient = useClient();

    const pageDescription = `
    Search the Stay Plugged In network of collegiate esports programs to find 
    your best fit. Each college member on the platform has a detailed profile 
    with all the needed program information to simplify your decision making 
    process. Use the search bar to find a particular college, or scroll through 
    the extensive list. Then, click on a college's profile to see more information!
    `;

    const SearchBarProvider = useMemo(() => createSearchBarProvider(SearchBarContext), []);
    const searchHistory = useCollegeSearchHistoryProvider();

    // the defaults in the context will be from the history provider. so that has to contain EVERYTHING.

    const [currentFilterDetails, setCurrentFilterDetails] = useImmer<CollegeSearchFilterState>({
        ...searchHistory.collegeFilters,
    });

    const buildSearchCriteria = useCallback(
        (searchText: string, filtersActive: boolean, isSubbedSearch: boolean) => {
            let termSearch: Espin_College_Bool_Exp | null = null;
            if (searchText !== '') {
                termSearch = {
                    _or: [
                        { program_name: { _ilike: `%${searchText}%` } },
                        { name: { _ilike: `%${searchText}%` } },
                        { address: { city: { _ilike: `%${searchText}%` } } },
                        { address: { state: { _ilike: `%${searchText}%` } } },
                    ],
                };
            }

            let distanceSearch: Espin_College_Bool_Exp | null = null;
            if (currentFilterDetails.useMaxDistance && !!user) {
                distanceSearch = {
                    address: {
                        location: {
                            _st_d_within: {
                                distance: currentFilterDetails.maxDistance * CONVERT_TO_MILES,
                                from: {
                                    type: 'Point',
                                    coordinates: userAddress?.location?.coordinates ?? [0, 0],
                                },
                            },
                        },
                    },
                };
            }

            let scholarshipsSearch: Espin_College_Bool_Exp | null = null;
            if (currentFilterDetails.scholarships) {
                scholarshipsSearch = {
                    scholarships: { _eq: currentFilterDetails.scholarships },
                };
            }

            let internationalSearch: Espin_College_Bool_Exp | null = null;
            if (currentFilterDetails.useInternationalSearch) {
                internationalSearch = {
                    address: { country: { _neq: 'United States' } },
                };
            }

            let gamesSearch: Espin_College_Bool_Exp | null = null;
            if (currentFilterDetails.gameFilters && currentFilterDetails.gameFilters.length > 0) {
                gamesSearch = {
                    college_games: {
                        game: {
                            game_definition: {
                                _in: currentFilterDetails.gameFilters,
                            },
                        },
                    },
                };
            }

            let verifiedSearch: Espin_College_Bool_Exp = { verified: { _eq: true } };
            let slugSearch: Espin_College_Bool_Exp = { slug: { _is_null: false } };
            let subbedSearch: Espin_College_Bool_Exp = {
                _or: [
                    { subscribed: { _eq: true } },
                    { subscription: { status: { _eq: Espin_Subscription_Status_Enum.Active } } },
                ],
            };
            let unsubbedSearch: Espin_College_Bool_Exp = {
                _and: [
                    { subscribed: { _eq: false } },
                    {
                        _or: [
                            { subscription: { status: { _neq: Espin_Subscription_Status_Enum.Active } } },
                            {
                                _not: {
                                    subscription: {},
                                },
                            },
                        ],
                    },
                ],
            };

            const base: Espin_College_Bool_Exp = {
                _and: [
                    subbedSearch && isSubbedSearch && subbedSearch,
                    unsubbedSearch && !isSubbedSearch && unsubbedSearch,
                    verifiedSearch && verifiedSearch,
                    slugSearch && slugSearch,
                    termSearch && termSearch,
                    scholarshipsSearch && scholarshipsSearch,
                    internationalSearch && internationalSearch,
                    distanceSearch && distanceSearch,
                    gamesSearch && gamesSearch,
                ].filter((s) => !!s),
            };

            return base;
        },
        [currentFilterDetails, userAddress, user],
    );

    // Grab the subbed count only + game definitions
    const [result] = useQuery<Colleges_IndexQuery>({
        query: Colleges_IndexDocument,
    });

    const { subbedCount, espin_game_definition } = result.data ?? {
        espin_game_definition: [],
        subbedCount: {
            aggregate: {
                count: 0,
            },
        },
    };

    const searchColleges = useCallback(
        async (s: string, filtersActive: boolean, offset: number = 0, isLoadMore: boolean = false) => {
            const unsubbedCriteria = buildSearchCriteria(s, filtersActive, false);
            const subbedCriteria = buildSearchCriteria(s, filtersActive, true);

            let unsubbedOffset = 0;
            // ALWAYS return unsubbed, but filter results
            const unsubbedPageSize = COLLEGES_PER_PAGE;
            const subbedPageSize = COLLEGES_PER_PAGE;
            const subbedOffset = offset;

            if (offset + COLLEGES_PER_PAGE > subbedCount.aggregate.count) {
                // subbed results are first, so set the offset to be the max of 0 or the number - subbed results
                unsubbedOffset = Math.max(...[0, offset - subbedCount.aggregate.count]);
            }

            const r = await gqlClient
                .query<SearchCollegesQuery, SearchCollegesQueryVariables>(
                    SearchCollegesDocument,
                    {
                        unsubbedSearch: unsubbedCriteria,
                        subbedSearch: subbedCriteria,
                        subbedOffset,
                        subbedPageSize,
                        unsubbedOffset,
                        unsubbedPageSize,
                    },
                    {
                        requestPolicy: 'network-only',
                    },
                )
                .toPromise();

            mixpanelRecordEvent({
                event: MixpanelEvents.COLLEGE_SEARCH,
                properties: {
                    distinct_id: user?.id,
                    timestamp: new Date().toISOString(),
                    user_role: user?.role,
                    user_id: user?.id,
                    scholarships_available: currentFilterDetails.scholarships,
                    text: s,
                    distance_input: currentFilterDetails.useMaxDistance ? currentFilterDetails.maxDistance : null,
                    international_search: currentFilterDetails.useInternationalSearch,
                    game_filter_input: currentFilterDetails.gameFilters,
                    show_more: isLoadMore,
                    //no graduating class, or state
                },
            });
            if (!!r.error) {
                toast.error('Error in search');

                searchHistory.setCollegeSearchHistory((draft) => {
                    draft.hasSearched = true;
                    draft.previousSearchCount = 0;
                    draft.previousSearchData = [];
                });
                return {
                    results: [],
                    count: 0,
                };
            }

            const { unsubbedColleges, subbedColleges, allCount } = r.data;

            // always show subbed results first
            // for unsubbed, only display if there are not enough subbed to fill out the page.
            const allColleges = [
                ...subbedColleges,
                ...unsubbedColleges.slice(0, COLLEGES_PER_PAGE - subbedColleges.length),
            ];

            searchHistory.setCollegeSearchHistory((draft) => {
                draft.collegeFilters = {
                    gameFilters: currentFilterDetails.gameFilters,
                    useMaxDistance: currentFilterDetails.useMaxDistance,
                    scholarships: currentFilterDetails.scholarships,
                    maxDistance: currentFilterDetails.maxDistance,
                    useInternationalSearch: currentFilterDetails.useInternationalSearch,
                };
                draft.hasSearched = true;
                draft.collegeSearch = s;
                draft.previousSearchCount = allCount.aggregate.count;
                draft.previousSearchData = isLoadMore ? [...draft.previousSearchData, ...allColleges] : allColleges;
                draft.previousSearchOffset = isLoadMore ? draft.previousSearchOffset + COLLEGES_PER_PAGE : 0;
            });

            return {
                results: allColleges,
                count: allCount.aggregate.count,
            };
        },

        [buildSearchCriteria, gqlClient, subbedCount, searchHistory, currentFilterDetails, user?.id, user?.role],
    );

    const collegeSearchReducer = useCallback(function (
        draft: WritableDraft<{
            offset: number;
            hasSearched: boolean;
            searchData: CollegeCardFragment[];
            fullData: CollegeCardFragment[];
            hasMore: boolean;
            lastSearchFiltersActive: boolean;
            lastSearchText: string;
        }>,
        action: SearchActionType,
    ) {
        switch (action.type) {
            case 'asyncSearchCompleted':
                return {
                    offset: 0,
                    hasSearched: true,
                    fullData: action.results,
                    hasMore: action.totalResults > COLLEGES_PER_PAGE,
                    searchData: action.results,
                    lastSearchText: action.lastSearchTerm,
                    lastSearchFiltersActive: action.filtersActive,
                };

            case 'asyncLoadMoreCompleted':
                const newData = [...draft.searchData, ...action.results];
                return {
                    offset: action.offset,
                    fullData: newData,
                    hasSearched: true,
                    hasMore: action.totalResults > COLLEGES_PER_PAGE + action.offset,
                    searchData: newData,
                    lastSearchText: action.lastSearchTerm,
                    lastSearchFiltersActive: action.filtersActive,
                };

            default:
                break;
        }
    }, []);

    const seoOverride: CustomSEO = {
        title: 'Colleges',
        description: pageDescription,
    };

    return (
        <Layout seoOverride={seoOverride}>
            <PageHeader title={'Colleges'} description={pageDescription} className="colleges-page-header" />

            <SearchBarProvider
                initialData={{
                    hasSearched: searchHistory.hasSearched,
                    lastSearchFiltersActive: false,
                    offset: searchHistory.previousSearchOffset,
                    hasMore:
                        searchHistory.previousSearchData.length >
                        searchHistory.previousSearchOffset + COLLEGES_PER_PAGE,
                    searchData: searchHistory.previousSearchData,
                    fullData: searchHistory.previousSearchData,
                    lastSearchText: searchHistory.collegeSearch,
                }}
                reducer={collegeSearchReducer}
            >
                <SearchInitializer search={searchColleges} />
                <PageListHeader
                    hasSearchButton={false}
                    className="colleges-page-list-header"
                    isStandaloneHeader={false}
                    title={'Search For Colleges'}
                    searchPlaceholder={'Search by Name'}
                    defaultValue={searchHistory.collegeSearch}
                    filtersAlwaysActive={true}
                    filters={useMemo(
                        () => [
                            <CollegeSearchFilterComponent
                                onChange={(d) => setCurrentFilterDetails(d)}
                                gameData={{ espin_game_definition }}
                                defaults={searchHistory.collegeFilters}
                                key="college-search"
                            />,
                        ],
                        [espin_game_definition, searchHistory.collegeFilters, setCurrentFilterDetails],
                    )}
                    customAsyncSearch={searchColleges}
                />
                <CollegeSearchResultsDisplay searchColleges={searchColleges} />
            </SearchBarProvider>
        </Layout>
    );
}

// This is ONLY run on initial page load when there's no history. We lose the SSG version, but it's fine.
function SearchInitializer({
    search,
}: {
    search: (s: string, filtersActive: boolean, offset: number, isLoadMore: boolean) => Promise<any>;
}) {
    // get our context
    const { dispatch, hasSearched } = useCollegeSearchContext();

    //

    useEffectOnLoad(() => {
        if (!hasSearched) {
            (async () => {
                const { results, count } = await search('', false, 0, false);
                dispatch({
                    type: 'asyncSearchCompleted',
                    results: results,
                    totalResults: count,
                    lastSearchTerm: '',
                    filtersActive: false,
                });
            })();
        }
    });
    return <></>;
}

export async function getStaticProps(ctx) {
    return UrqlSSGForPage<Colleges_IndexQuery>(Colleges_IndexDocument);
}

export function useCollegeSearchContext(): SearchBarContextType<CollegeCardFragment> {
    return useContext(SearchBarContext);
}
